import { getXMCloudDeployBaseUrl } from '@sitecore-ui/portal-singular';
import { hostnames, env } from 'features/common/config/envConfig';

const ENVIRONMENT = 'environment';
const environmentsBaseUrl =
  getXMCloudDeployBaseUrl(env) + '/api/environments/v1';

export function getContextApiUrl() {
  const contextApiBaseUrl = {
    [hostnames.localhost]:
      'https://edge-platform-context-api-staging.sitecore-staging.cloud/api/context/user/v1',
    [hostnames.internal]:
      'https://edge-platform-context-api-staging.sitecore-staging.cloud/api/context/user/v1',
    [hostnames.dev]:
      'https://edge-platform-context-api-staging.sitecore-staging.cloud/api/context/user/v1',
    [hostnames.qa]:
      'https://edge-platform-context-api-staging.sitecore-staging.cloud/api/context/user/v1',
    [hostnames.staging]:
      'https://edge-platform-context-api-staging.sitecore-staging.cloud/api/context/user/v1',
    [hostnames.beta]:
      'https://edge-platform-context-api-pre-production.sitecorecloud.io/api/context/user/v1',
    [hostnames['pre-production']]:
      'https://edge-platform-context-api-pre-production.sitecorecloud.io/api/context/user/v1',
    [hostnames.production]:
      'https://edge-platform-context-api.sitecorecloud.io/api/context/user/v1',
  }[window.location.hostname];

  return contextApiBaseUrl || '';
}

const contextIdApiBaseUrl = getContextApiUrl();
// List endpoint moved to V2 for cursor pagination + server-side search; other endpoints remain V1.
const contextIdApiV2BaseUrl = contextIdApiBaseUrl.replace(/\/v1$/, '/v2');

//TODO: Add the correct scope for the contextIdConfig

export type ListSitecoreContextsQueryParams = {
  search?: string;
  continuationToken?: string;
  pageSize?: number;
};

export const contextIdConfig = {
  get_contextids: {
    url: ({
      search,
      continuationToken,
      pageSize,
    }: ListSitecoreContextsQueryParams = {}) => {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (continuationToken) params.set('continuationToken', continuationToken);
      if (pageSize) params.set('pageSize', String(pageSize));
      const queryString = params.toString();
      return `${contextIdApiV2BaseUrl}/contexts${
        queryString ? `?${queryString}` : ''
      }`;
    },
    // Base query key kept stable (no params) so invalidateQueries(queryKey) still
    // matches every paged/searched variant via react-query's prefix matching.
    queryKey: ['contexts/list'] as const,
    scope: 'ep.usr.ctx:r',
  },
  get_contextid: {
    url: (sitecoreContextId: string) =>
      `${contextIdApiBaseUrl}/contexts/${sitecoreContextId}`,
    queryKey: (sitecoreContextId: string) =>
      [`contexts/context/${sitecoreContextId}`] as const,
    scope: 'ep.usr.ctx:r',
  },
  get_context_resources: {
    url: (sitecoreContextId: string) =>
      `${contextIdApiBaseUrl}/contexts/${sitecoreContextId}/resources`,
    queryKey: (sitecoreContextId: string) =>
      [`contexts/context/${sitecoreContextId}/resources`] as const,
    scope: 'ep.usr.ctx_res:r',
  },
  create_contextid: {
    url: `${contextIdApiBaseUrl}/contexts`,
    scope: 'ep.usr.ctx:w',
  },
  edit_contextid: {
    url: (sitecoreContextId: string) =>
      `${contextIdApiBaseUrl}/contexts/${sitecoreContextId}`,
    scope: 'ep.usr.ctx:w',
  },
  delete_contextid: {
    url: (sitecoreContextId: string) =>
      `${contextIdApiBaseUrl}/contexts/${sitecoreContextId}`,
    scope: 'ep.usr.ctx:w',
  },
  create_scoped_context: {
    url: (sitecoreContextId: string) =>
      `${contextIdApiBaseUrl}/contexts/${sitecoreContextId}/filtered`,
    scope: 'ep.usr.ctx:w',
  },
  get_scoped_contexts: {
    url: (sitecoreContextId: string) =>
      `${contextIdApiBaseUrl}/contexts/${sitecoreContextId}/filtered`,
    queryKey: (sitecoreContextId: string) => [
      'filtered_contexts/list',
      sitecoreContextId,
    ],
    scope: 'ep.usr.ctx:r',
  },
  get_scoped_context: {
    url: (sitecoreContextId: string, scopeId: string) =>
      `${contextIdApiBaseUrl}/contexts/${sitecoreContextId}/filtered/${scopeId}`,
    queryKey: (sitecoreContextId: string, scopeId: string) =>
      [
        `contexts/scoped-contexts/${sitecoreContextId}`,
        `filtered/${scopeId}`,
      ] as const,
    scope: 'ep.usr.ctx:r',
  },
  edit_scoped_context: {
    url: (sitecoreContextId: string, scopeId: string) =>
      `${contextIdApiBaseUrl}/contexts/${sitecoreContextId}/filtered/${scopeId}`,
    scope: 'ep.usr.ctx:w',
  },
  delete_scoped_context: {
    url: (sitecoreContextId: string, scopeId: string) =>
      `${contextIdApiBaseUrl}/contexts/${sitecoreContextId}/filtered/${scopeId}`,
    scope: 'ep.usr.ctx:w',
  },
  get_available_resources: {
    url: `${contextIdApiBaseUrl}/available-resources`,
    queryKey: ['resources/list'],
    scope: 'ep.usr.ctx_res:r',
  },
  get_available_products: {
    url: `${contextIdApiBaseUrl}/available-products`,
    queryKey: ['products/list'],
    scope: 'ep.usr.ctx_res:r',
  },
  create_context_resource: {
    url: (sitecoreContextId: string) =>
      `${contextIdApiBaseUrl}/contexts/${sitecoreContextId}/resources`,
    scope: 'ep.usr.ctx_res:w',
  },
  upsert_context_product: {
    url: (sitecoreContextId: string) =>
      `${contextIdApiBaseUrl}/contexts/${sitecoreContextId}/products`,
    scope: 'ep.usr.ctx_res:w',
  },
};

export const environments = {
  environment: {
    scope: 'xmclouddeploy.environments:manage',
    url: (environmentId: string) => `${environmentsBaseUrl}/${environmentId}`,
    queryKey: (environmentId: string) => [ENVIRONMENT, environmentId] as const,
  },
  regenerate_contextid: {
    scope: 'xmclouddeploy.environments:manage',
    url: (environmentId: string) =>
      `${environmentsBaseUrl}/${environmentId}/regenerate-context`,
    queryKey: (environmentId: string) =>
      [ENVIRONMENT, 'contextid', environmentId] as const,
  },
};
